import { ReactElement, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import GroupsTable from './GroupsTable';
import SearchInput from '../controls/SearchInput';
import Filter from '../controls/Filter';
import { deleteGroup } from '../services/dashboard-service';
import { ActionData, Group } from '../constants/telosair-interfaces';
import AddGroupModal from '../modals/AddGroup';
import Loader from './loader';
import { useData } from '../context/data.context';
import { TOAST_MESSAGES } from '../constants/global-constants';

/**
 * Groups component renders the list of groups and devices installed in it.
 * @returns - Groups Component.
 */
export default function Groups(): ReactElement {
  const { groupedData: groups, fetchAllGroups } = useData();
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionData, setActionData] = useState<ActionData<Group>>({} as ActionData<Group>);

  /**
   * Close the modal.
   * @param newData - Wheather a new group is added.
   */
  const handleClose = (newData?: boolean): void => {
    if (newData) {
      fetchAllGroups();
    }
    setIsModalOpen(false);
    setActionData({} as ActionData<Group>);
  };

  /**
   * Function to delete a group.
   * @param id - ID of the group.
   */
  const handleDeleteGroup = async (id: number): Promise<void> => {
    try {
      setLoading(true);
      await deleteGroup(id);
      await fetchAllGroups();
      setLoading(false);
      toast.success(TOAST_MESSAGES.deleted);
    } catch (error) {
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
   * Function to operate delete and edit group.
   * @param action - Action data needs type and group data.
   */
  const handleAction = async (action: ActionData<Group>): Promise<void> => {
    if (action.type === 'delete') {
      handleDeleteGroup(action.data.groupId);
    } else {
      setActionData(action);
      setIsModalOpen(true);
    }
  };
  return (
    <>
      <div className="flex flex-col sm:flex-row gap-4 justify-between rounded bg-white dark:bg-container-500 p-4 mt-4">
        <div className="sm:flex hidden items-center space-x-4 invisible">
          <SearchInput textValue={searchText} setText={setSearchText} />
          <Filter position="left" />
        </div>
        <div className="mr-4 flex items-center">
          <Button
            theme="primary"
            size="sm"
            name="Create Group"
            onClick={() => {
              setIsModalOpen(true);
            }}
            isBlockButton
          />
        </div>
      </div>
      {loading && (
        <div>
          <Loader />
        </div>
      )}
      <div className="mt-4">
        <GroupsTable
          setIsGroupModalOpen={(isOpen) => setIsModalOpen(isOpen)}
          handleAction={handleAction}
          groups={groups}
        />
      </div>
      {isModalOpen && <AddGroupModal actionData={actionData} handleClose={handleClose} />}{' '}
    </>
  );
}
